import { useEffect, useRef } from 'preact/hooks';
import * as THREE from 'three';
import { PET_BY_ID } from '@vm/content';
import { PetState } from '@vm/shared';
import { PetRig, petMesh } from '../../game/render/petRig';
import { previewRenderer, type PreviewHandle, type PreviewScene } from '../three/previewRenderer';
import { Spin } from '../three/spin';
import { addStudioLights, makeShadowBlob, previewMaterials } from '../three/stage';
import '../../styles/three-ui.css';

/** Menu preview of a companion pet (optionally in a pet style): the in-game voxel model, hovering and slowly turning. */
export interface PetPreviewProps {
  petId: string;
  /** Pet style id ('' or missing = the pet's own colors). */
  styleId?: string;
  /** Square size in CSS pixels (default 96). */
  size?: number;
  className?: string;
  /** Turntable spin (default: on for previews of 48 px and up). */
  rotate?: boolean;
  /** Drag to rotate (default: on for previews of 48 px and up). */
  interactive?: boolean;
  /** Starting turn in radians (default: a three-quarter view of the face). */
  yaw?: number;
}

/** Below this size previews are drawn once as a still three-quarter view (icons in slots/lists). */
const STILL_BELOW = 48;
const FOV = 30;
const ELEVATION = 0.22;
/** Three-quarter view of the face (pets face -Z; the camera looks from -Z). */
const START_YAW = -0.6;

class PetStage implements PreviewScene {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(FOV, 1, 0.02, 20);
  readonly spin: Spin;
  /** Animate wings/rotors and bob (false = a still image). */
  animated = true;
  private readonly turn = new THREE.Group();
  private readonly shadow: THREE.Mesh;
  private rig: PetRig | null = null;
  private key = '';
  private aspect = 1;
  private readonly box = new THREE.Box3(new THREE.Vector3(-0.3, -0.3, -0.3), new THREE.Vector3(0.3, 0.3, 0.3));
  private t = Math.random() * 10;
  private drawn = false;

  constructor(yaw: number) {
    this.spin = new Spin(yaw);
    addStudioLights(this.scene, 1.05);
    this.shadow = makeShadowBlob(1, 1, 0.55);
    this.scene.add(this.turn, this.shadow);
  }

  setPet(petId: string, styleId: string | undefined): void {
    const key = `${petId}|${styleId ?? ''}`;
    if (key === this.key) return;
    this.key = key;
    if (this.rig) {
      this.turn.remove(this.rig.root);
      this.rig.dispose();
      this.rig = null;
    }
    // Geometry is the game's shared pet mesh cache (per pet + style): never disposed here.
    const mesh = petMesh(petId, styleId || undefined);
    if (!mesh) return;
    this.rig = new PetRig(mesh, previewMaterials.lit, previewMaterials.glow);
    this.turn.add(this.rig.root);
    this.box.makeEmpty();
    for (const g of [mesh.lit, mesh.glow]) {
      if (!g) continue;
      if (!g.boundingBox) g.computeBoundingBox();
      this.box.union(g.boundingBox!);
    }
    // The model center sits on the spin axis; frame the box around it.
    this.drawn = false;
    this.frame();
  }

  tick(dt: number): boolean {
    const turned = this.spin.tick(dt);
    this.turn.rotation.y = this.spin.yaw;
    if (!this.animated) {
      const first = !this.drawn;
      this.drawn = true;
      return first || turned;
    }
    this.t += dt;
    if (this.rig) {
      this.rig.animate(dt, PetState.Follow);
      this.rig.root.position.y = Math.sin(this.t * 2.1) * 0.018;
      this.rig.body.rotation.z = Math.sin(this.t * 1.3) * 0.05;
    }
    return true;
  }

  resize(w: number, h: number): void {
    this.aspect = w / Math.max(1, h);
    this.frame();
  }

  /** Fits the turning model: the widest horizontal extent and the height both stay in view. */
  private frame(): void {
    const cam = this.camera;
    cam.aspect = this.aspect;
    const b = this.box;
    const rh = Math.max(Math.hypot(b.min.x, b.min.z), Math.hypot(b.max.x, b.max.z), Math.hypot(b.min.x, b.max.z), Math.hypot(b.max.x, b.min.z)) * 1.04;
    const top = b.max.y + 0.03;
    const bottom = b.min.y - 0.03;
    const tv = Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    const th = tv * this.aspect;
    const half = (top - bottom) / 2;
    // Width: the turning model sweeps a cylinder of radius rh. Height: its front edge is nearer.
    const margin = 0.94;
    const d = Math.max(rh / Math.sin(Math.atan(th * margin)), half / (tv * margin) + rh * 0.55);
    const cy = (top + bottom) / 2;
    cam.position.set(0, cy + Math.sin(ELEVATION) * d, -Math.cos(ELEVATION) * d);
    cam.lookAt(0, cy, 0);
    cam.updateProjectionMatrix();
    this.shadow.position.y = bottom - 0.1;
    this.shadow.scale.set(rh * 1.7, 1, rh * 1.7);
    this.drawn = false;
  }

  dispose(): void {
    if (this.rig) {
      this.turn.remove(this.rig.root);
      this.rig.dispose();
      this.rig = null;
    }
  }
}

export function PetPreview(props: PetPreviewProps) {
  const size = props.size ?? 96;
  const pet = PET_BY_ID[props.petId];
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<PetStage | null>(null);
  const handleRef = useRef<PreviewHandle | null>(null);
  const still = size < STILL_BELOW;
  const interactive = (props.interactive ?? !still) && !!pet;

  if (!stageRef.current) stageRef.current = new PetStage(props.yaw ?? START_YAW);
  const stage = stageRef.current;

  useEffect(() => {
    const handle = previewRenderer.attach(canvasRef.current!, stage);
    handleRef.current = handle;
    return () => {
      handle.detach();
      handleRef.current = null;
      stage.dispose();
      stageRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!interactive) return;
    return stage.spin.attach(canvasRef.current!, (on) => handleRef.current?.setInteracting(on));
  }, [interactive]);

  useEffect(() => {
    stage.setPet(props.petId, props.styleId);
    handleRef.current?.invalidate();
  }, [props.petId, props.styleId]);

  useEffect(() => {
    stage.animated = !still;
    stage.spin.auto = (props.rotate ?? !still) ? 0.55 : 0;
    handleRef.current?.invalidate();
  }, [props.rotate, still]);

  return (
    <span
      class={`pet-preview three-preview${pet ? '' : ' is-empty'} ${props.className ?? ''}`}
      style={{ width: `${size}px`, height: `${size}px`, flex: 'none', display: 'inline-block' }}
      title={pet?.name}
      aria-hidden={pet ? undefined : 'true'}
    >
      <canvas ref={canvasRef} class={`three-canvas${interactive ? ' spinnable' : ''}`} />
    </span>
  );
}
